import { useState, useEffect } from "react";
import { Mic, CheckCircle, Sparkles, Languages, Volume2, Zap, Download, Copy, Mail, MessageSquare, FileText, ListChecks, ClipboardPaste } from "lucide-react";

// ==================== VOICE TRANSCRIPTION ====================
export function VoiceTranscriptionDemo() {
  const [phase, setPhase] = useState(0); // 0: idle, 1: recording, 2: transcribing, 3: done
  const [displayText, setDisplayText] = useState("");
  const fullText = "Hello, this is a voice memo for my meeting notes tomorrow.";

  useEffect(() => {
    let timeout;
    let charIndex = 0;
    let isMounted = true;
    
    const animateCycle = () => {
      if (!isMounted) return;
      charIndex = 0;
      setDisplayText("");
      setPhase(0);

      // Phase 1: Recording
      timeout = setTimeout(() => {
        if (!isMounted) return;
        setPhase(1);
        
        // Phase 2: Transcribing
        timeout = setTimeout(() => {
          if (!isMounted) return;
          setPhase(2);
          
          // Phase 3: Text appears
          timeout = setTimeout(() => {
            if (!isMounted) return;
            setPhase(3);
            
            const typeChar = () => {
              if (!isMounted) return;
              if (charIndex < fullText.length) {
                setDisplayText(fullText.slice(0, charIndex + 1));
                charIndex++;
                timeout = setTimeout(typeChar, 40);
              } else {
                timeout = setTimeout(animateCycle, 3000);
              }
            };
            
            typeChar();
          }, 1000);
        }, 1500);
      }, 800);
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  return (
    <div className="h-32 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-2.5 h-2.5 rounded-full ${phase === 1 ? 'bg-red-500 animate-pulse' : phase === 2 ? 'bg-yellow-500 animate-pulse' : phase === 3 ? 'bg-green-500' : 'bg-gray-600'}`} />
        <span className="text-xs font-medium" style={{ color: phase === 1 ? '#ef4444' : phase === 2 ? '#f59e0b' : phase === 3 ? '#22c55e' : '#718096' }}>
          {phase === 0 ? 'Ready' : phase === 1 ? 'Recording...' : phase === 2 ? 'Transcribing...' : 'Complete'}
        </span>
      </div>
      <div className="flex-1 p-3 rounded-lg text-sm" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
        {phase >= 3 ? (
          <>
            <span style={{ color: '#22d3ee' }}>{displayText}</span>
            {displayText.length < fullText.length && <span className="animate-pulse" style={{ color: '#22d3ee' }}>|</span>}
          </>
        ) : phase === 2 ? (
          <span className="flex items-center gap-2" style={{ color: '#f59e0b' }}>
            <span className="animate-pulse">Processing audio...</span>
          </span>
        ) : phase === 1 ? (
          <span className="flex items-center gap-2" style={{ color: '#ef4444' }}>
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            Listening...
          </span>
        ) : (
          <span style={{ color: '#4a5568' }}>Press record to start...</span>
        )}
      </div>
    </div>
  );
}

// ==================== FIX ERRORS ====================
export function FixErrorsDemo() {
  const [phase, setPhase] = useState(0);
  const [displayText, setDisplayText] = useState("");
  
  const original = "hey i wanted to tell you that the meeting is at 2 pm its really important";
  const fixed = "Hey, I wanted to tell you that the meeting is at 2 PM. It's really important.";

  useEffect(() => {
    let timeout;
    let isMounted = true;
    
    const runAnimation = () => {
      if (!isMounted) return;
      setPhase(0);
      setDisplayText(original);
      
      timeout = setTimeout(() => {
        if (!isMounted) return;
        setPhase(1); // Fixing
        
        timeout = setTimeout(() => {
          if (!isMounted) return;
          setPhase(2); // Fixed
          setDisplayText(fixed);
          
          timeout = setTimeout(runAnimation, 3000);
        }, 1000);
      }, 2500);
    };

    runAnimation();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  return (
    <div className="h-32 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle className="w-4 h-4" style={{ color: phase === 2 ? '#22c55e' : '#f59e0b' }} />
        <span className="text-xs font-medium" style={{ color: phase === 2 ? '#22c55e' : '#f59e0b' }}>
          {phase === 0 ? 'Before' : phase === 1 ? 'Fixing...' : 'Fixed!'}
        </span>
      </div>
      <div 
        className="flex-1 p-3 rounded-lg text-sm overflow-hidden"
        style={{ 
          backgroundColor: '#1a2332', 
          border: `1px solid ${phase === 2 ? '#22c55e40' : '#2d3748'}`,
          color: phase === 2 ? '#22c55e' : '#a0aec0'
        }}
      >
        {displayText}
      </div>
    </div>
  );
}

// ==================== CHANGE TONE ====================
export function ToneChangeDemo() {
  const [selectedTone, setSelectedTone] = useState(0);
  const [displayText, setDisplayText] = useState("");
  
  const tones = [
    { name: "Casual", color: "#22d3ee", text: "Hey! Just checking in about the project. Let me know!" },
    { name: "Professional", color: "#8b5cf6", text: "I am following up regarding the project status. Please advise." },
    { name: "Friendly", color: "#22c55e", text: "Hi there! Hope you're well! How's the project going?" },
    { name: "Direct", color: "#f59e0b", text: "Project update needed. Send feedback today." },
  ];

  useEffect(() => {
    let timeout;
    let isMounted = true;
    let currentTone = 0;
    
    const animateCycle = () => {
      if (!isMounted) return;
      setSelectedTone(currentTone);
      setDisplayText(tones[currentTone].text);
      
      timeout = setTimeout(() => {
        if (!isMounted) return;
        currentTone = (currentTone + 1) % tones.length;
        animateCycle();
      }, 2500);
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  const currentColor = tones[selectedTone].color;

  return (
    <div className="h-32 flex flex-col">
      {/* Tone Pills */}
      <div className="flex gap-1.5 mb-2">
        {tones.map((tone, idx) => (
          <span
            key={tone.name}
            className="px-2 py-0.5 rounded-full text-[10px] font-medium"
            style={{ 
              backgroundColor: selectedTone === idx ? tone.color : '#2d3748',
              color: selectedTone === idx ? '#fff' : '#718096'
            }}
          >
            {tone.name}
          </span>
        ))}
      </div>
      {/* Text */}
      <div 
        className="flex-1 p-3 rounded-lg text-sm"
        style={{ backgroundColor: '#1a2332', border: `1px solid ${currentColor}40`, color: currentColor }}
      >
        {displayText}
      </div>
    </div>
  );
}

// ==================== TRANSLATE ====================
export function TranslateDemo() {
  const [currentLang, setCurrentLang] = useState(0);
  
  const translations = [
    { flag: "🇺🇸", lang: "English", text: "Hello! How are you today?" },
    { flag: "🇪🇸", lang: "Spanish", text: "¡Hola! ¿Cómo estás hoy?" },
    { flag: "🇫🇷", lang: "French", text: "Bonjour! Comment allez-vous?" },
    { flag: "🇩🇪", lang: "German", text: "Hallo! Wie geht es Ihnen?" },
    { flag: "🇯🇵", lang: "Japanese", text: "こんにちは！お元気ですか？" },
  ];

  useEffect(() => {
    let timeout;
    let isMounted = true;
    let langIdx = 0;
    
    const animateCycle = () => {
      if (!isMounted) return;
      setCurrentLang(langIdx);
      
      timeout = setTimeout(() => {
        langIdx = (langIdx + 1) % translations.length;
        animateCycle();
      }, 2000);
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  const current = translations[currentLang];

  return (
    <div className="h-32 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{current.flag}</span>
        <span className="text-xs font-medium" style={{ color: '#3b82f6' }}>{current.lang}</span>
        {/* Language dots */}
        <div className="flex gap-1 ml-auto">
          {translations.map((_, idx) => (
            <div 
              key={idx}
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: currentLang === idx ? '#3b82f6' : '#2d3748' }}
            />
          ))}
        </div>
      </div>
      <div 
        className="flex-1 p-3 rounded-lg text-sm"
        style={{ backgroundColor: '#1a2332', border: '1px solid #3b82f640', color: '#3b82f6' }}
      >
        {current.text}
      </div>
    </div>
  );
}

// ==================== READ ALOUD (Translate → Read) ====================
export function ReadAloudDemo() {
  const [phase, setPhase] = useState(0); // 0: English, 1: translating, 2: Spanish reading
  const [highlightedWord, setHighlightedWord] = useState(-1);
  
  const english = "Hey John, I need to pick up the kids from school today.";
  const spanish = "Oye Juan, necesito recoger a los niños de la escuela hoy.";

  useEffect(() => {
    let timeout;
    let isMounted = true;
    
    const runCycle = () => {
      if (!isMounted) return;
      setPhase(0);
      setHighlightedWord(-1);
      let wordIdx = 0;
      
      // Read English
      const readEnglish = () => {
        if (!isMounted) return;
        const words = english.split(' ');
        if (wordIdx < words.length) {
          setHighlightedWord(wordIdx);
          wordIdx++;
          timeout = setTimeout(readEnglish, 300);
        } else {
          // Translate
          timeout = setTimeout(() => {
            if (!isMounted) return;
            setPhase(1);
            setHighlightedWord(-1);
            
            // Show Spanish
            timeout = setTimeout(() => {
              if (!isMounted) return;
              setPhase(2);
              wordIdx = 0;
              
              // Read Spanish
              const readSpanish = () => {
                if (!isMounted) return;
                const words = spanish.split(' ');
                if (wordIdx < words.length) {
                  setHighlightedWord(wordIdx);
                  wordIdx++;
                  timeout = setTimeout(readSpanish, 300);
                } else {
                  timeout = setTimeout(runCycle, 2000);
                }
              };
              timeout = setTimeout(readSpanish, 500);
            }, 800);
          }, 500);
        }
      };
      
      timeout = setTimeout(readEnglish, 500);
    };

    runCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  const currentText = phase === 2 ? spanish : english;
  const words = currentText.split(' ');

  return (
    <div className="h-32 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <Volume2 className="w-4 h-4" style={{ color: '#22c55e' }} />
        <span className="text-xs font-medium" style={{ color: '#22c55e' }}>
          {phase === 0 ? '🇺🇸 English' : phase === 1 ? 'Translating...' : '🇪🇸 Spanish'}
        </span>
      </div>
      <div 
        className="flex-1 p-3 rounded-lg text-sm"
        style={{ backgroundColor: '#1a2332', border: '1px solid #22c55e40' }}
      >
        {phase === 1 ? (
          <div className="flex items-center gap-2" style={{ color: '#3b82f6' }}>
            <Languages className="w-4 h-4" />
            <span>Translating to Spanish...</span>
          </div>
        ) : (
          words.map((word, idx) => (
            <span 
              key={idx}
              className={idx === highlightedWord ? 'bg-green-500/30 rounded px-0.5' : ''}
              style={{ color: idx <= highlightedWord ? '#22c55e' : '#a0aec0' }}
            >
              {word}{idx < words.length - 1 ? ' ' : ''}
            </span>
          ))
        )}
      </div>
      <div className="text-center mt-1 text-[10px]" style={{ color: '#718096' }}>
        Translate → Read Aloud
      </div>
    </div>
  );
}

// ==================== PASTE OR UPLOAD TEXT ====================
export function UploadPasteDemo() {
  const [phase, setPhase] = useState(0);
  const [method, setMethod] = useState("paste");
  
  const original = "teh meeting is schedled for wendsday at 2pm";
  const fixed = "The meeting is scheduled for Wednesday at 2 PM.";

  useEffect(() => {
    let timeout;
    let isMounted = true;
    let currentMethod = 0;
    const methods = ["paste", "upload"];
    
    const animateCycle = () => {
      if (!isMounted) return;
      setMethod(methods[currentMethod]);
      setPhase(0);
      
      timeout = setTimeout(() => {
        if (!isMounted) return;
        setPhase(1); // Show original
        
        timeout = setTimeout(() => {
          if (!isMounted) return;
          setPhase(2); // Fixing
          
          timeout = setTimeout(() => {
            if (!isMounted) return;
            setPhase(3); // Fixed
            
            timeout = setTimeout(() => {
              currentMethod = (currentMethod + 1) % methods.length;
              animateCycle();
            }, 2000);
          }, 800);
        }, 1500);
      }, 800);
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  return (
    <div className="h-32 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <span 
          className="px-2 py-0.5 rounded-full text-[10px] font-medium"
          style={{ backgroundColor: method === 'paste' ? '#8b5cf6' : '#2d3748', color: method === 'paste' ? '#fff' : '#718096' }}
        >
          Paste Text
        </span>
        <span 
          className="px-2 py-0.5 rounded-full text-[10px] font-medium"
          style={{ backgroundColor: method === 'upload' ? '#8b5cf6' : '#2d3748', color: method === 'upload' ? '#fff' : '#718096' }}
        >
          Upload Doc
        </span>
        <span className="text-xs ml-auto" style={{ color: phase === 3 ? '#22c55e' : '#718096' }}>
          {phase === 0 ? 'Ready' : phase === 1 ? (method === 'paste' ? 'Pasted' : 'Uploaded') : phase === 2 ? 'Fixing...' : 'Fixed!'}
        </span>
      </div>
      <div 
        className="flex-1 p-3 rounded-lg text-sm overflow-hidden"
        style={{ 
          backgroundColor: '#1a2332', 
          border: `1px solid ${phase === 3 ? '#22c55e40' : '#2d3748'}`,
          color: phase === 3 ? '#22c55e' : phase >= 1 ? '#ef4444' : '#a0aec0'
        }}
      >
        {phase === 0 ? `${method === 'paste' ? 'Paste' : 'Upload'} text to fix, translate, change tone...` : 
         phase <= 2 ? original : fixed}
      </div>
    </div>
  );
}

// ==================== OUTPUT FORMATS ====================
export function OutputFormatDemo() {
  const [formatIdx, setFormatIdx] = useState(0);
  
  const formats = [
    { 
      id: "message", 
      name: "Message", 
      icon: <MessageSquare className="w-3 h-3" />, 
      color: "#22d3ee",
      text: "Hey! Meeting at 3 PM tomorrow. Don't forget the reports! 👍"
    },
    { 
      id: "email", 
      name: "Email", 
      icon: <Mail className="w-3 h-3" />, 
      color: "#8b5cf6",
      text: "To: team@company.com\nSubject: Meeting Tomorrow\n\nHi Team,\nMeeting at 3 PM tomorrow."
    },
    { 
      id: "list", 
      name: "Todo List", 
      icon: <ListChecks className="w-3 h-3" />, 
      color: "#22c55e",
      text: "• Schedule meeting for 3 PM\n• Prepare reports\n• Send reminder to team"
    },
  ];

  useEffect(() => {
    let timeout;
    let isMounted = true;
    let idx = 0;
    
    const animateCycle = () => {
      if (!isMounted) return;
      setFormatIdx(idx);
      
      timeout = setTimeout(() => {
        idx = (idx + 1) % formats.length;
        animateCycle();
      }, 2500);
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  const current = formats[formatIdx];

  return (
    <div className="h-32 flex flex-col">
      {/* Format Pills */}
      <div className="flex gap-1.5 mb-2">
        {formats.map((format, idx) => (
          <span
            key={format.id}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
            style={{ 
              backgroundColor: formatIdx === idx ? format.color : '#2d3748',
              color: formatIdx === idx ? '#fff' : '#718096'
            }}
          >
            {format.icon}
            {format.name}
          </span>
        ))}
      </div>
      {/* Text */}
      <div 
        className="flex-1 p-3 rounded-lg text-xs overflow-hidden"
        style={{ backgroundColor: '#1a2332', border: `1px solid ${current.color}40`, color: current.color }}
      >
        <pre className="whitespace-pre-wrap font-sans">{current.text}</pre>
      </div>
    </div>
  );
}

// ==================== SHORTCUTS ====================
export function ShortcutsDemo() {
  const [phase, setPhase] = useState(0); // 0: typing text, 1: type trigger, 2: expanding, 3: expanded
  const [displayText, setDisplayText] = useState("");
  
  const beforeText = "Thanks for your help. ";
  const trigger = "/sig";
  const signature = "\n\nBest regards,\nJohn Smith\nSenior Developer";

  useEffect(() => {
    let timeout;
    let isMounted = true;
    let charIdx = 0;
    
    const animateCycle = () => {
      if (!isMounted) return;
      setPhase(0);
      setDisplayText("");
      charIdx = 0;
      
      // Phase 0: Type the message
      const typeMessage = () => {
        if (!isMounted) return;
        if (charIdx < beforeText.length) {
          setDisplayText(beforeText.slice(0, charIdx + 1));
          charIdx++;
          timeout = setTimeout(typeMessage, 60);
        } else {
          // Phase 1: Type the trigger
          timeout = setTimeout(() => {
            if (!isMounted) return;
            setPhase(1);
            charIdx = 0;
            
            const typeTrigger = () => {
              if (!isMounted) return;
              if (charIdx < trigger.length) {
                setDisplayText(beforeText + trigger.slice(0, charIdx + 1));
                charIdx++;
                timeout = setTimeout(typeTrigger, 150);
              } else {
                // Phase 2: Expanding
                timeout = setTimeout(() => {
                  if (!isMounted) return;
                  setPhase(2);
                  
                  // Phase 3: Show expanded
                  timeout = setTimeout(() => {
                    if (!isMounted) return;
                    setPhase(3);
                    setDisplayText(beforeText + signature);
                    
                    timeout = setTimeout(animateCycle, 3500);
                  }, 600);
                }, 500);
              }
            };
            
            typeTrigger();
          }, 500);
        }
      };
      
      timeout = setTimeout(typeMessage, 500);
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  return (
    <div className="h-32 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-4 h-4" style={{ color: phase === 3 ? '#22c55e' : '#6366f1' }} />
        <span className="text-xs font-medium" style={{ color: phase === 3 ? '#22c55e' : '#6366f1' }}>
          {phase === 0 ? 'Typing...' : phase === 1 ? 'Trigger: /sig' : phase === 2 ? 'Expanding...' : 'Expanded!'}
        </span>
        {phase === 1 && (
          <span className="ml-auto px-1.5 py-0.5 rounded text-[9px]" style={{ backgroundColor: '#6366f120', color: '#a78bfa' }}>
            Shortcut detected
          </span>
        )}
      </div>
      <div 
        className="flex-1 p-3 rounded-lg text-sm overflow-hidden"
        style={{ backgroundColor: '#1a2332', border: `1px solid ${phase === 3 ? '#22c55e40' : '#6366f140'}` }}
      >
        <pre className="whitespace-pre-wrap font-sans" style={{ color: phase === 3 ? '#22c55e' : phase === 1 ? '#a78bfa' : '#a0aec0' }}>
          {displayText}
          {phase < 2 && <span className="animate-pulse">|</span>}
        </pre>
      </div>
    </div>
  );
}

// ==================== DOWNLOAD ====================
export function DownloadDemo() {
  const [selectedFormat, setSelectedFormat] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const formats = [
    { name: "TXT", color: "#3b82f6" },
    { name: "PDF", color: "#ef4444" },
    { name: "DOCX", color: "#22c55e" },
  ];

  useEffect(() => {
    let timeout;
    let isMounted = true;
    let formatIdx = 0;
    
    const animateCycle = () => {
      if (!isMounted) return;
      setSelectedFormat(formatIdx);
      setProgress(0);
      setIsDownloading(true);
      
      let prog = 0;
      const updateProgress = () => {
        if (!isMounted) return;
        if (prog < 100) {
          prog += 10;
          setProgress(prog);
          timeout = setTimeout(updateProgress, 100);
        } else {
          setIsDownloading(false);
          timeout = setTimeout(() => {
            formatIdx = (formatIdx + 1) % formats.length;
            animateCycle();
          }, 1500);
        }
      };
      
      timeout = setTimeout(updateProgress, 500);
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  return (
    <div className="h-32 flex flex-col">
      <div className="flex gap-2 mb-2">
        {formats.map((fmt, idx) => (
          <span
            key={fmt.name}
            className="px-2 py-0.5 rounded text-[10px] font-medium"
            style={{ 
              backgroundColor: selectedFormat === idx ? fmt.color : '#2d3748',
              color: selectedFormat === idx ? '#fff' : '#718096'
            }}
          >
            {fmt.name}
          </span>
        ))}
      </div>
      <div 
        className="flex-1 p-3 rounded-lg flex flex-col justify-center"
        style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
      >
        <div className="w-full h-2 rounded-full bg-gray-700 overflow-hidden mb-2">
          <div 
            className="h-full rounded-full transition-all duration-100"
            style={{ width: `${progress}%`, backgroundColor: formats[selectedFormat].color }}
          />
        </div>
        <span className="text-xs text-center" style={{ color: formats[selectedFormat].color }}>
          {isDownloading ? `Downloading ${formats[selectedFormat].name}... ${progress}%` : `${formats[selectedFormat].name} Downloaded!`}
        </span>
      </div>
    </div>
  );
}

// ==================== EMOJI SHORTCUTS DEMO ====================
export function EmojiShortcutsDemo() {
  const [phase, setPhase] = useState(0);
  const [displayText, setDisplayText] = useState("");
  
  const stages = [
    { text: '"Great job thumbs up"', emoji: false },
    { text: '"Great job 👍"', emoji: true },
    { text: '"I love this heart"', emoji: false },
    { text: '"I love this ❤️"', emoji: true },
  ];

  useEffect(() => {
    let timeout;
    let isMounted = true;
    
    const animateCycle = () => {
      if (!isMounted) return;
      let idx = 0;
      
      const nextStage = () => {
        if (!isMounted) return;
        setPhase(idx % 2);
        setDisplayText(stages[idx].text);
        idx++;
        if (idx < stages.length) {
          timeout = setTimeout(nextStage, 1200);
        } else {
          timeout = setTimeout(animateCycle, 2000);
        }
      };
      
      nextStage();
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  return (
    <div className="h-32 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{phase === 1 ? '✨' : '🎤'}</span>
        <span className="text-xs font-medium" style={{ color: phase === 1 ? '#f59e0b' : '#718096' }}>
          {phase === 1 ? 'Emoji Inserted!' : 'Say trigger word...'}
        </span>
      </div>
      <div className="flex-1 p-3 rounded-lg" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
        <p className="text-sm" style={{ color: phase === 1 ? '#f59e0b' : '#a0aec0' }}>
          {displayText}
        </p>
      </div>
    </div>
  );
}

// ==================== CREATE TYPES DEMO ====================
export function CreateTypesDemo() {
  const [phase, setPhase] = useState(0);
  const [displayText, setDisplayText] = useState("");
  
  const types = [
    { trigger: '"Create email to John..."', output: 'To: John\nSubject: Meeting\n\nHi John,...' },
    { trigger: '"Create todo list..."', output: '• Item 1\n• Item 2\n• Item 3' },
  ];

  useEffect(() => {
    let timeout;
    let isMounted = true;
    let typeIndex = 0;
    
    const animateCycle = () => {
      if (!isMounted) return;
      
      // Show trigger
      setPhase(0);
      setDisplayText(types[typeIndex].trigger);
      
      timeout = setTimeout(() => {
        if (!isMounted) return;
        // Show formatted output
        setPhase(1);
        setDisplayText(types[typeIndex].output);
        
        timeout = setTimeout(() => {
          typeIndex = (typeIndex + 1) % types.length;
          animateCycle();
        }, 2500);
      }, 1500);
    };

    animateCycle();
    return () => { isMounted = false; clearTimeout(timeout); };
  }, []);

  return (
    <div className="h-32 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{phase === 1 ? '📝' : '🎤'}</span>
        <span className="text-xs font-medium" style={{ color: phase === 1 ? '#a78bfa' : '#718096' }}>
          {phase === 1 ? 'Auto-formatted!' : 'Say "Create..."'}
        </span>
      </div>
      <div className="flex-1 p-3 rounded-lg text-xs" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748', whiteSpace: 'pre-line' }}>
        <span style={{ color: phase === 1 ? '#a78bfa' : '#a0aec0' }}>
          {displayText}
        </span>
      </div>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function AnimatedFeatureCard({ type }) {
  const features = {
    transcription: {
      icon: <Mic className="h-6 w-6" />,
      title: "Voice Transcription",
      description: "Speak naturally and watch your words appear instantly.",
      color: "#22d3ee",
      Demo: VoiceTranscriptionDemo
    },
    errors: {
      icon: <CheckCircle className="h-6 w-6" />,
      title: "Auto-Fix Errors",
      description: "Automatically fixes punctuation, capitalization, and grammar.",
      color: "#f59e0b",
      Demo: FixErrorsDemo
    },
    tone: {
      icon: <Sparkles className="h-6 w-6" />,
      title: "Change Tone",
      description: "Transform your text to any tone with one click.",
      color: "#8b5cf6",
      Demo: ToneChangeDemo
    },
    translate: {
      icon: <Languages className="h-6 w-6" />,
      title: "Translate",
      description: "Instant translation to 40+ languages.",
      color: "#3b82f6",
      Demo: TranslateDemo
    },
    readaloud: {
      icon: <Volume2 className="h-6 w-6" />,
      title: "Read Aloud",
      description: "Hear your text spoken back in natural voice.",
      color: "#22c55e",
      Demo: ReadAloudDemo
    },
    uploadpaste: {
      icon: <ClipboardPaste className="h-6 w-6" />,
      title: "Paste or Upload Text",
      description: "Paste text or upload docs to fix, translate, change tone.",
      color: "#8b5cf6",
      Demo: UploadPasteDemo
    },
    outputformat: {
      icon: <Mail className="h-6 w-6" />,
      title: "Output Formats",
      description: "Format as message, email, or todo list.",
      color: "#14b8a6",
      Demo: OutputFormatDemo
    },
    shortcuts: {
      icon: <Zap className="h-6 w-6" />,
      title: "Shortcuts",
      description: "Save and insert common phrases instantly.",
      color: "#6366f1",
      Demo: ShortcutsDemo
    },
    download: {
      icon: <Download className="h-6 w-6" />,
      title: "Download",
      description: "Export your text as TXT, PDF, or DOCX.",
      color: "#ef4444",
      Demo: DownloadDemo
    },
    emojishortcuts: {
      icon: <span className="text-xl">😊</span>,
      title: "Emoji Shortcuts",
      description: "Say 'thumbs up' or 'heart' to insert emojis.",
      color: "#f59e0b",
      Demo: EmojiShortcutsDemo
    },
    createtypes: {
      icon: <Mail className="h-6 w-6" />,
      title: "Create Types",
      description: "Say 'Create email' or 'Create todo' to auto-format.",
      color: "#a78bfa",
      Demo: CreateTypesDemo
    },
  };

  const feature = features[type];
  if (!feature) return null;

  const { icon, title, description, color, Demo } = feature;

  return (
    <div 
      className="p-4 rounded-xl transition-all hover:scale-[1.02] h-full"
      style={{ backgroundColor: '#0f1419', border: '1px solid #2d3748' }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="p-2 rounded-lg"
          style={{ backgroundColor: `${color}20`, color }}
        >
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-sm">{title}</h3>
          <p className="text-xs" style={{ color: '#718096' }}>{description}</p>
        </div>
      </div>
      <Demo />
    </div>
  );
}
